import { WorkExperienceRole, WorkExperienceCompany } from '@applicator/shared'

import {
  toggleRoleLocationInCompany,
  toggleRolePositionInCompany,
} from '../../../utils/workExperienceInclusion'

// Helper function to update role in company for toggle
export function updateRoleInCompanyForToggle(
  company: WorkExperienceCompany,
  roleId: string,
  field: WorkExperienceRoleToggleField
): WorkExperienceCompany {
  if (field === 'positionIncluded') {
    return toggleRolePositionInCompany(company, roleId)
  }
  return toggleRoleLocationInCompany(company, roleId)
}

// Helper function to update employment type in role
export function updateEmploymentTypeInRole(role: WorkExperienceRole, employmentType: string): WorkExperienceRole {
  return { ...role, employmentType, employmentTypeIncluded: true }
}

// Helper function to update role in company for employment type
export function updateRoleInCompanyForEmploymentType(
  company: WorkExperienceCompany,
  roleId: string,
  employmentType: string
): WorkExperienceCompany {
  return {
    ...company,
    roles: (company.roles || []).map((role) =>
      role.id === roleId ? updateEmploymentTypeInRole(role, employmentType) : role
    ),
  }
}

// Helper function to remove role from company
export function removeRoleFromCompany(company: WorkExperienceCompany, roleId: string): WorkExperienceCompany {
  return {
    ...company,
    roles: (company.roles || []).filter((r) => r.id !== roleId),
  }
}

// Helper function to update all roles in company
export function updateAllRolesInCompany(
  company: WorkExperienceCompany,
  roleId: string,
  field: WorkExperienceRoleField,
  value: string,
  updateSingleRoleField: (role: WorkExperienceRole, roleId: string, field: WorkExperienceRoleField, value: string) => WorkExperienceRole | null
): WorkExperienceCompany {
  return {
    ...company,
    roles: (company.roles || []).map((role) => {
      const updatedRole = updateSingleRoleField(role, roleId, field, value)
      return updatedRole || role
    }),
  }
}

// Helper function to update company or return original for employment type
export function updateCompanyOrReturnOriginalForEmploymentType(
  company: WorkExperienceCompany,
  companyId: string,
  roleId: string,
  employmentType: string
): WorkExperienceCompany {
  if (company.id === companyId) {
    return updateRoleInCompanyForEmploymentType(company, roleId, employmentType)
  }
  return company
}

// Helper function to map companies with updated employment type
export function mapCompaniesWithUpdatedEmploymentType(
  companiesToMap: WorkExperienceCompany[],
  companyId: string,
  roleId: string,
  employmentType: string
): WorkExperienceCompany[] {
  return companiesToMap.map((company) => updateCompanyOrReturnOriginalForEmploymentType(company, companyId, roleId, employmentType))
}

// Helper function to update company or return original for role deletion
export function updateCompanyOrReturnOriginalForRoleDeletion(
  company: WorkExperienceCompany,
  companyId: string,
  roleId: string
): WorkExperienceCompany {
  if (company.id === companyId) {
    return removeRoleFromCompany(company, roleId)
  }
  return company
}

// Helper function to map companies with deleted role
export function mapCompaniesWithDeletedRole(
  companiesToMap: WorkExperienceCompany[],
  companyId: string,
  roleId: string
): WorkExperienceCompany[] {
  return companiesToMap.map((company) => updateCompanyOrReturnOriginalForRoleDeletion(company, companyId, roleId))
}

// Helper function to update role in company for bullet deletion
export function updateRoleInCompanyForBulletDeletion(company: WorkExperienceCompany, roleId: string, bulletId: string, removeBulletFromRole: (role: WorkExperienceRole, bulletId: string) => WorkExperienceRole): WorkExperienceCompany {
  return {
    ...company,
    roles: (company.roles || []).map((role) =>
      role.id === roleId ? removeBulletFromRole(role, bulletId) : role
    ),
  }
}

// Helper function to update company or return original for bullet deletion
export function updateCompanyOrReturnOriginalForBulletDeletion(
  company: WorkExperienceCompany,
  companyId: string,
  roleId: string,
  bulletId: string,
  removeBulletFromRole: (role: WorkExperienceRole, bulletId: string) => WorkExperienceRole
): WorkExperienceCompany {
  if (company.id === companyId) {
    return updateRoleInCompanyForBulletDeletion(company, roleId, bulletId, removeBulletFromRole)
  }
  return company
}

// Helper function to map companies with deleted bullet
export function mapCompaniesWithDeletedBullet(
  companiesToMap: WorkExperienceCompany[],
  companyId: string,
  roleId: string,
  bulletId: string,
  removeBulletFromRole: (role: WorkExperienceRole, bulletId: string) => WorkExperienceRole
): WorkExperienceCompany[] {
  return companiesToMap.map((company) => updateCompanyOrReturnOriginalForBulletDeletion(company, companyId, roleId, bulletId, removeBulletFromRole))
}

// Helper function to update company or return original
export function updateCompanyOrReturnOriginal(
  company: WorkExperienceCompany,
  companyId: string,
  roleId: string,
  field: WorkExperienceRoleField,
  value: string,
  updateAllRolesInCompany: (company: WorkExperienceCompany, roleId: string, field: WorkExperienceRoleField, value: string, updateSingleRoleField: (role: WorkExperienceRole, roleId: string, field: WorkExperienceRoleField, value: string) => WorkExperienceRole | null) => WorkExperienceCompany,
  updateSingleRoleField: (role: WorkExperienceRole, roleId: string, field: WorkExperienceRoleField, value: string) => WorkExperienceRole | null
): WorkExperienceCompany {
  if (company.id !== companyId) return company
  return updateAllRolesInCompany(company, roleId, field, value, updateSingleRoleField)
}

// Helper function to map companies with updated role
export function mapCompaniesWithUpdatedRole(
  companiesToMap: WorkExperienceCompany[],
  companyId: string,
  roleId: string,
  field: WorkExperienceRoleField,
  value: string,
  updateAllRolesInCompany: (company: WorkExperienceCompany, roleId: string, field: WorkExperienceRoleField, value: string, updateSingleRoleField: (role: WorkExperienceRole, roleId: string, field: WorkExperienceRoleField, value: string) => WorkExperienceRole | null) => WorkExperienceCompany,
  updateSingleRoleField: (role: WorkExperienceRole, roleId: string, field: WorkExperienceRoleField, value: string) => WorkExperienceRole | null
): WorkExperienceCompany[] {
  return companiesToMap.map((company) => updateCompanyOrReturnOriginal(company, companyId, roleId, field, value, updateAllRolesInCompany, updateSingleRoleField))
}
